class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);

/* Olive Girl: desktop mouse hover for existing native header menus only.
 * Keeps the original details click/keyboard behavior and Qikify configuration.
 */
(() => {
  if (window.oliveGirlNativeHoverInstalled) return;
  window.oliveGirlNativeHoverInstalled = true;

  const desktopMouse = window.matchMedia(
    '(min-width: 990px) and (hover: hover) and (pointer: fine)'
  );
  const selector = '.og-subnav .header__inline-menu > .list-menu > li > header-menu';
  let activeMenu = null;
  let openTimer = null;
  let closeTimer = null;
  let keyboardNavigation = false;

  document.addEventListener('keydown', (event) => {
    if (['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      keyboardNavigation = true;
    }
  });
  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse') keyboardNavigation = false;
  });

  function cancelTimers() {
    window.clearTimeout(openTimer);
    window.clearTimeout(closeTimer);
    openTimer = null;
    closeTimer = null;
  }

  function closeMenu(menu) {
    if (!menu) return;
    const details = menu.querySelector('details');
    if (!details) return;
    if (typeof menu.close === 'function') menu.close();
    else details.open = false;
    const summary = details.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');
  }

  function queueClose(menu) {
    cancelTimers();
    closeTimer = window.setTimeout(() => {
      if (activeMenu !== menu) return;
      if (menu.matches(':hover') || (keyboardNavigation && menu.contains(document.activeElement))) return;
      closeMenu(menu);
      activeMenu = null;
    }, 220);
  }

  document.addEventListener('pointerover', (event) => {
    if (!desktopMouse.matches || event.pointerType !== 'mouse') return;
    if (!(event.target instanceof Element)) return;
    const menu = event.target.closest(selector);

    if (!menu) {
      if (!activeMenu) return;
      const nav = activeMenu.closest('.og-subnav');
      if (nav && nav.contains(event.target)) {
        cancelTimers();
        if (!(keyboardNavigation && activeMenu.contains(document.activeElement))) {
          closeMenu(activeMenu);
          activeMenu = null;
        }
      }
      return;
    }

    if (menu === activeMenu) {
      window.clearTimeout(closeTimer);
      return;
    }

    // Do not hide a different menu while keyboard focus is inside its panel.
    const focusedPeer = keyboardNavigation && Array.from(document.querySelectorAll(selector)).find(
      (peer) => peer !== menu && peer.contains(document.activeElement)
    );
    if (focusedPeer) return;

    cancelTimers();
    if (activeMenu) closeMenu(activeMenu);
    activeMenu = menu;
    openTimer = window.setTimeout(() => {
      if (!desktopMouse.matches || activeMenu !== menu || !menu.isConnected) return;
      if (!menu.matches(':hover')) return;
      const details = menu.querySelector('details');
      const summary = details && details.querySelector('summary');
      if (!details || !summary) return;
      document.querySelectorAll(selector).forEach((peer) => {
        if (peer !== menu) closeMenu(peer);
      });
      details.open = true;
      summary.setAttribute('aria-expanded', 'true');
    }, 120);
  });

  document.addEventListener('pointerout', (event) => {
    if (!desktopMouse.matches || event.pointerType !== 'mouse' || !activeMenu) return;
    const target = event.target;
    if (!(target instanceof Node) || !activeMenu.contains(target)) return;
    if (event.relatedTarget instanceof Node && activeMenu.contains(event.relatedTarget)) return;
    queueClose(activeMenu);
  });

  // Changing to a touch or narrow viewport must not leave a hover-open panel.
  desktopMouse.addEventListener('change', () => {
    cancelTimers();
    if (!desktopMouse.matches && activeMenu) {
      closeMenu(activeMenu);
      activeMenu = null;
    }
  });
})();
